import { useMemo, useState } from "react";
import classNames from "./UsageGraph.module.css";
import { ResponsiveBarCanvas } from "@nivo/bar";
import { Usage } from "../../../../hooks/api-new";
import { useTimeIntervals } from "../../../../hooks/usage";
import { TimeRange } from "./TimeRangeSelect";
import Select from "../../../common/Select";
import { getLanguageName } from "../../../../types";

export default function ParticipantUsageGraph(props: {
  timeRange: TimeRange;
  usage: Usage[];
}) {
  const languages = useMemo(() => {
    return [...new Set(props.usage.map((usage) => usage.language))];
  }, [props.usage]);

  const [selectedLanguage, setSelectedLanguage] = useState<string>();

  const timeIntervals = useTimeIntervals(props.timeRange);

  const data = useMemo(() => {
    return props.usage
      .filter(
        (usage) => !selectedLanguage || usage.language === selectedLanguage
      )
      .reduce(
        (
          acc: {
            interval: { name: string; start: number; end: number };
            participants: Set<string>;
          }[],
          usage
        ) => {
          // Get the time interval(s) the participant was active in
          const start = new Date(usage.created_at).getTime();
          const end = new Date(usage.ended_at).getTime();

          const relevantIntervals = timeIntervals.filter(
            (interval) => start <= interval.end && end >= interval.start
          );

          relevantIntervals.forEach((interval) => {
            const existing = acc.find((item) => item.interval === interval);

            // Use set to count unique participants
            if (existing) {
              existing.participants.add(usage.livekit_sid || "");
            } else {
              acc.push({
                interval: interval,
                participants: new Set([usage.livekit_sid || ""]),
              });
            }
          });

          return acc;
        },
        // Pre-fill with empty data
        timeIntervals.map((t) => ({
          interval: t,
          participants: new Set<string>(),
        }))
      )
      .sort((a, b) => a.interval.start - b.interval.start)
      .map((d) => ({
        interval: d.interval.name,
        participants: d.participants.size,
      }));
  }, [props.usage, selectedLanguage, timeIntervals]);

  return (
    <div className={classNames.UsageGraph}>
      <div className={classNames.Header}>
        <h3>Total number of participants</h3>
        {languages.length >= 2 && (
          <Select
            items={[
              { value: undefined, label: "All languages" },
              ...languages.map((l) => ({
                value: l,
                label: getLanguageName(l)!,
              })),
            ]}
            selected={selectedLanguage}
            onChange={(value) => setSelectedLanguage(value as string)}
            align="end"
            color="gray"
            backgroundColor="black"
            borderColor="dark-gray"
          />
        )}
      </div>
      <div>
        <ResponsiveBarCanvas
          data={data}
          keys={["participants"]}
          indexBy="interval"
          margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
          padding={0.7}
          colors="#3c75ee"
          axisLeft={{ format: (i) => (Math.floor(i) === i ? i : "") }}
          axisBottom={{
            format: (interval: string) => {
              const index = data.findIndex((d) => d.interval === interval);
              return index === 0 || index === data.length - 1 ? interval : "";
            },
          }}
          enableLabel={false}
          legendLabel={() => "Number of participants"}
          tooltip={({ value, data }) => (
            <>
              {data.interval}: {Math.round(value)} participants
            </>
          )}
          theme={{
            grid: {
              line: {
                stroke: "#555",
              },
            },
            axis: {
              ticks: {
                text: {
                  fill: "#ccc",
                },
              },
              legend: {
                text: {
                  fill: "#ccc",
                },
              },
            },
            tooltip: {
              container: {
                color: "#ccc",
              },
            },
          }}
          legends={[
            {
              dataFrom: "keys",
              itemTextColor: "#ccc",
              anchor: "top-left", // Position the legend in the top left
              direction: "row", // Place the legend items in a row
              justify: false,
              translateX: -20,
              translateY: -40, // Adjust the position to avoid overlap with bars
              itemsSpacing: 10, // Space between legend items
              itemWidth: 100,
              itemHeight: 20,
              symbolSize: 12,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemOpacity: 0.85,
                  },
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  );
}
